import dynamic from 'next/dynamic';
import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ForwardedRef,
  forwardRef,
  useMemo,
} from 'react';
import { Icons } from '../Icon/constants';

const Icon = dynamic(() => import('../Icon')) as any;

type ButtonElementProps = ButtonHTMLAttributes<HTMLButtonElement>;
type AnchorElementProps = AnchorHTMLAttributes<HTMLAnchorElement>;

export type Size = 'small' | 'medium' | 'large' | 'xl';

export interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'ghost';
  outline?: boolean;
  size?: 'xs' | 'small' | 'medium' | 'large' | 'xl';
  icon?: Icons;
  iconPosition?: 'left' | 'right';
  disabled?: boolean;
}

export type Props = (AnchorElementProps | ButtonElementProps) & ButtonProps;

const BASE_STYLES =
  'rounded-lg font-semibold flex items-center justify-center border-2 focus:ring-4 focus:outline-none transition-shadow transition-colors';

const sizeMap = {
  default: {
    xs: 'h-6 py-1 px-2 text-sm',
    small: 'h-8 py-1 px-4 text-base',
    medium: 'h-10 py-2 px-5 text-base',
    large: 'h-12 py-3 px-6 text-base',
    xl: 'h-14 py-4 px-8 text-base',
  },
  iconOnly: {
    xs: 'h-6 p-1',
    small: 'h-8 p-2',
    medium: 'h-10 p-3',
    large: 'h-12 p-4',
    xl: 'h-14 p-4',
  },
};

const variantMap = {
  fill: {
    primary:
      'text-gray-50 border-transparent bg-pink-500 focus:ring-pink-600/20 focus:bg-pink-600 hover:bg-pink-600',
    secondary:
      'text-gray-50 border-transparent bg-teal-500 focus:ring-teal-600/20 focus:bg-teal-600 hover:bg-teal-600',
    tertiary:
      'text-gray-50 border-transparent bg-gray-400 focus:ring-gray-500/20 focus:bg-gray-500 hover:bg-gray-500',
    ghost:
      'text-gray-400 border-transparent bg-gray-200 focus:ring-gray-200/20',
  },
  outline: {
    primary:
      'text-pink-500 border-pink-500 bg-transparent hover:bg-pink-500/10 focus:bg-pink-500/20 focus:ring-pink-600/20',
    secondary:
      'text-teal-500 border-teal-500 bg-transparent hover:bg-teal-500/10 focus:bg-teal-500/20 focus:ring-teal-600/20',
    tertiary:
      'text-gray-400 border-gray-400 bg-transparent hover:bg-gray-400/10 focus:bg-gray-500/20 focus:ring-gray-400/20',
    ghost:
      'text-gray-400 border-gray-200 bg-transparent hover:bg-gray-200/10 focus:bg-gray-200/20 focus:ring-gray-200/20',
  },
};

export const Button = forwardRef(function Button(
  {
    variant = 'primary',
    outline = false,
    size = 'medium',
    children,
    icon,
    iconPosition = 'left',
    className,
    disabled,
    ...props
  }: Props,
  ref: ForwardedRef<any>
) {
  const styles = useMemo(() => {
    return `${BASE_STYLES} ${
      sizeMap[icon && !children ? 'iconOnly' : 'default'][size]
    } ${variantMap[outline ? 'outline' : 'fill'][variant]} ${
      disabled ? 'opacity-50' : ''
    } ${className ?? ''}`;
  }, [variant, outline, size, disabled, children, icon, className]);

  const iconSize = useMemo(() => {
    if (!children) {
      if (size === 'large' || size === 'xl') {
        return 24;
      }
    }
    return 16;
  }, [size, children]);

  const iconMargin =
    iconPosition === 'left'
      ? size === 'xs'
        ? 'mr-1'
        : 'mr-2'
      : size === 'xs'
      ? 'ml-1'
      : 'ml-2';

  const buttonIcon = icon ? (
    <Icon name={icon} className={children ? iconMargin : ''} size={iconSize} />
  ) : null;

  if ('href' in props) {
    return (
      <a {...(props as AnchorElementProps)} ref={ref} className={`${styles}`}>
        {iconPosition === 'left' && buttonIcon}
        {children}
        {iconPosition === 'right' && buttonIcon}
      </a>
    );
  } else {
    return (
      <button
        type="button"
        disabled={disabled}
        {...(props as ButtonElementProps)}
        ref={ref}
        className={styles}
      >
        {iconPosition === 'left' && buttonIcon}
        {children}
        {iconPosition === 'right' && buttonIcon}
      </button>
    );
  }
});

export default Button;
